import type { PublicProduct } from './checkout'
import type { StorefrontAddOn, StorefrontPlan } from './storefront'

/**
 * How closed a section or a space is, from the most open to the most closed.
 *
 * `private` is the fourth value and the one with a `who` behind it: a set of
 * grants naming member groups, products or subscription groups. The order
 * matters — a space may never be more open than the section holding it, and the
 * server refuses it if it tries.
 */
export type Visibility = 'public' | 'members' | 'plan' | 'private'

/** From the most open to the most closed. The order is the rule. */
export const VISIBILITY_ORDER: Visibility[] = ['public', 'members', 'plan', 'private']

export const visibilityRank = (visibility: Visibility): number => {
  const rank = VISIBILITY_ORDER.indexOf(visibility)
  return rank === -1 ? VISIBILITY_ORDER.length : rank
}

/** What a space may be, given the section that holds it. */
export const allowedVisibilitiesUnder = (sectionVisibility: Visibility): Visibility[] =>
  VISIBILITY_ORDER.filter((value) => visibilityRank(value) >= visibilityRank(sectionVisibility))

export interface AccessGrantGroup {
  id: string
  name: string
  color?: string | null
}

export interface AccessGrantProduct {
  id: string
  title: string
}

export interface AccessGrantSubscriptionGroup {
  id: string
  name: string
}

/** F-151a: a badge of the community's catalog that opens the scope (staff reads). */
export interface AccessGrantBadge {
  id: string
  name: string
  imageUrl?: string | null
  visibility?: 'public' | 'private'
}

/** A badge the refusal names: public ones only (a private one is just `hasHiddenBadge`). */
export interface DenialBadge {
  id: string
  name: string
  imageUrl?: string | null
  description?: string | null
}

/** F-151b: a level of the community's ladder that opens the scope, from that level up. */
export interface AccessLevel {
  /** `null` only for a default level not stored yet; the write then names it by `level`. */
  id: string | null
  /** The level's number, 1 = the first. */
  level: number
  name: string
  minPoints: number
  imageUrl?: string | null
}

/** The lists a scope grants, as the API returns them (`badges` since F-151a; older payloads lack it). */
export interface AccessGrants {
  groups: AccessGrantGroup[]
  products: AccessGrantProduct[]
  subscriptionGroups: AccessGrantSubscriptionGroup[]
  badges?: AccessGrantBadge[]
  /** F-151b: the minimum level (one per scope); `undefined` when the payload did not carry it. */
  minLevel?: AccessLevel | null
}

/**
 * The lists a scope grants, as the API takes them. `badgeIds` absent keeps the
 * badge grants on the server; `[]` clears them.
 */
export interface AccessGrantInput {
  groupIds: string[]
  productIds: string[]
  subscriptionGroupIds: string[]
  badgeIds?: string[]
  /** F-151b: absent keeps the server's level, `null` removes it. */
  minLevelId?: string | null
  /** F-151b: the level by its number, for a default level that has no id yet. */
  minLevel?: number | null
}

/**
 * What the section around a space requires.
 *
 * It combines with the space's own `access` by **AND**, not as more items in
 * the same list — hence the name. Whoever fails this never sees the space,
 * however well they satisfy what the space asks for on top.
 */
export interface RequiredBySection extends AccessGrants {
  sectionId: string
  sectionTitle: string
  visibility: Visibility
}

/**
 * Why a scope is drawn with a padlock instead of being hidden (F-161a): sign in
 * (a members area seen by a visitor), `plan` (any subscription), a product, a
 * specific plan, or a group (only the team grants it; its name never comes).
 */
export type LockReason = 'signIn' | 'plan' | 'product' | 'subscription' | 'group' | 'badge' | 'level'

/** What whoever has no access sees of a section (F-161a). Default `section_and_spaces`. */
export type SectionLockedDisplay = 'hide' | 'section' | 'section_and_spaces'
export const SECTION_LOCKED_DISPLAYS: SectionLockedDisplay[] = ['hide', 'section', 'section_and_spaces']

/** What whoever has no access sees of a space (F-161a). Default `inherit` (as the section says). */
export type SpaceLockedDisplay = 'inherit' | 'hide' | 'show'
export const SPACE_LOCKED_DISPLAYS: SpaceLockedDisplay[] = ['inherit', 'hide', 'show']

/** The next steps a refusal hints at (the 403's `data.actions`). */
export type DenialAction = 'signIn' | 'joinCommunity' | 'seePlans' | 'buy' | 'askTeam' | 'earnBadge' | 'earnPoints' | 'switchLanguage'

/**
 * Why the API refused a space or a section, as the 403's `data` (spaces) or
 * the `denial` of `GET /api/sections/:id` (a padlocked section) carry it.
 * Only what is shown padlocked comes with `lockReason` and the scope's name;
 * a hidden one answers `{ reason: 'other', actions: [] }`.
 */
export interface AccessDenial {
  /** `language` (F-252): access opens it, the viewer's language does not; `languages` says which ones do. */
  reason: 'plan' | 'group' | 'approval' | 'badge' | 'level' | 'language' | 'other'
  actions: DenialAction[]
  /** F-252: the content's languages, with `reason: 'language'`. */
  languages?: string[]
  /** F-151b: the level that opens it (a visitor gets it too). */
  level?: AccessLevel | null
  /** F-151b: the member's own way there; only signed in, with the gamification on. */
  progress?: LevelDenialProgress | null
  /** F-151a: the public badges that open it (a visitor gets them too). */
  badges?: DenialBadge[]
  /** F-151a: a private badge also opens it; never its name. */
  hasHiddenBadge?: boolean
  productIds?: string[]
  subscriptionGroupIds?: string[]
  lockReason?: LockReason | null
  /** F-161b: whether "Fale com a equipe" works here (chat on, someone on the team to answer). Absent means false. */
  canContactTeam?: boolean
  space?: { id: string; title: string; slug: string; icon?: string | null; description?: string | null; module?: string; sectionId?: string }
  section?: { id: string; title: string; slug: string; icon?: string | null; description?: string | null }
  /**
   * The store cards that open it, when the next step is to buy or subscribe
   * (`actions` has `buy` or `seePlans`), visitors included: the same shapes as
   * `GET /api/storefront/spaces/{id}/products`. Only what opens it, and only
   * what checkout takes; a plan reached by one billing option comes with that
   * option only. Never on a group-only space.
   */
  offers?: AccessOffers | null
}

/** F-151b: how far the member is from the level that opens the scope (the profile's numbers). */
export interface LevelDenialProgress {
  points: number
  pointsToGo: number
  currentLevel: number | null
  /** The level's activity requirements with this week's count; a space the member cannot open comes `spaceHidden`, without its name. */
  requirements: Array<{
    actionType: string
    spaceId: string | null
    spaceName: string | null
    spaceHidden?: boolean
    minCount: number
    thisWeek: number
  }>
}

export interface AccessOffers {
  plans: StorefrontPlan[]
  addOns: StorefrontAddOn[]
  products: PublicProduct[]
}

/** The denial a failed SDK call carries, or null. */
export const denialOf = (error: unknown): AccessDenial | null => {
  const data = (error as { response?: { data?: { data?: unknown } } } | null)?.response?.data?.data as AccessDenial | undefined
  return data && Array.isArray(data.actions) ? data : null
}

export const grantCount = (grants?: AccessGrants | null): number =>
  grants
    ? grants.groups.length + grants.products.length + grants.subscriptionGroups.length + (grants.badges?.length ?? 0) + (grants.minLevel ? 1 : 0)
    : 0

export const emptyAccessGrants = (): AccessGrants => ({
  groups: [],
  products: [],
  subscriptionGroups: [],
  badges: [],
  minLevel: null
})

export const toAccessGrantInput = (grants: AccessGrants): AccessGrantInput => ({
  groupIds: grants.groups.map((group) => group.id),
  productIds: grants.products.map((product) => product.id),
  subscriptionGroupIds: grants.subscriptionGroups.map((group) => group.id),
  /* Only when the badges were read: a payload without them must not wipe the server's. */
  ...(grants.badges ? { badgeIds: grants.badges.map((badge) => badge.id) } : {}),
  /* F-151b: the same for the level. `null` removes it; a default level without an id goes by its number. */
  ...(grants.minLevel === undefined
    ? {}
    : grants.minLevel === null
      ? { minLevelId: null }
      : grants.minLevel.id
        ? { minLevelId: grants.minLevel.id }
        : { minLevel: grants.minLevel.level })
})

/** The label key of each padlock reason, in the menu and on the offer (F-161a, criterion 12). */
export const lockReasonKey = (reason: LockReason | null | undefined): string | null =>
  reason ? `common:access.lockReason.${reason}` : null

/** A space this person can open: the API marks the others `locked` (F-161a). */
export const isOpenSpace = (space: { locked?: boolean }) => !space.locked
